import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  RefreshCw,
  Filter,
  ListChecks,
  FileText,
  Star,
  X,
  Save,
  CheckCircle2,
  Scale,
  Trophy,
  BrainCircuit,
  CheckCircle,
  XCircle,
} from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import SourcingRfpService from '../../services/sourcingRFPService';

const TECHNICAL_WEIGHT = 0.6;
const COMMERCIAL_WEIGHT = 0.4;

const clampScore = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
};

const toBidAmount = (response) =>
  (response.lineItems || []).reduce(
    (sum, item) => sum + (Number(item.price) || 0),
    0,
  );

const BidEvaluation = ({ isNested = false, rfpId: propRfpId }) => {
  const navigate = useNavigate();
  const {
    error: showError,
    success: showSuccess,
    toasts,
    removeToast,
  } = useToast();

  const [rfps, setRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState(propRfpId || '');
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);

  const [evalModal, setEvalModal] = useState({ open: false, response: null });
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [runningAi, setRunningAi] = useState(false);
  const [scores, setScores] = useState({
    technical: 0,
    commercial: 0,
    comments: '',
  });
  const showErrorRef = useRef(showError);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const loadAiAnalysis = useCallback(async (id) => {
    try {
      const res = await SourcingRfpService.getBidAnalysis(id);
      if (res.success) {
        setAiAnalysis(res.data);
      }
    } catch (err) {
      setAiAnalysis(null);
    }
  }, []);

  useEffect(() => {
    if (selectedRfpId) {
      loadAiAnalysis(selectedRfpId);
    } else {
      setAiAnalysis(null);
    }
  }, [selectedRfpId, loadAiAnalysis]);

  // Load RFPs and Initial Responses (All)
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both simultaneously
      const [rfpRes, responseRes] = await Promise.all([
        SourcingRfpService.getRfps(),
        SourcingRfpService.getResponses(''), // Empty string to get ALL
      ]);

      const rfpList =
        rfpRes.data?.items ||
        rfpRes.items ||
        (Array.isArray(rfpRes.data) ? rfpRes.data : []);
      setRfps(rfpList);
      setResponses(responseRes.data || []);
    } catch (err) {
      showErrorRef.current('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const onRfpSelect = async (id) => {
    setSelectedRfpId(id);
    setLoading(true);
    try {
      // If id is empty, getResponses('') returns everything
      const res = await SourcingRfpService.getResponses(id || '');
      setResponses(res.data || []);
    } catch (err) {
      showErrorRef.current('Failed to filter vendor bids');
    } finally {
      setLoading(false);
    }
  };

  const openEvalModal = (res) => {
    setEvalModal({ open: true, response: res });
    setScores({
      technical: res.evaluationId?.technicalScore ?? 0,
      commercial: res.evaluationId?.commercialScore ?? 0,
      comments: res.evaluationId?.comments || '',
    });
  };

  const handleScoreSubmit = async () => {
    try {
      const technical = clampScore(scores.technical);
      const commercial = clampScore(scores.commercial);
      const weighted =
        technical * TECHNICAL_WEIGHT + commercial * COMMERCIAL_WEIGHT;

      const payload = {
        responseId: evalModal.response._id,
        technicalScore: technical,
        commercialScore: commercial,
        weightedScore: weighted,
        comments: scores.comments,
      };

      await SourcingRfpService.submitEvaluation(
        evalModal.response.rfpId._id,
        payload,
      );
      showSuccess('Vendor scored successfully.');
      setEvalModal({ open: false, response: null });
      loadInitialData();
    } catch (err) {
      showError(err.message || 'Failed to submit score');
    }
  };

  const handleRunAiAnalysis = async () => {
    if (!selectedRfpId) return;
    setRunningAi(true);
    try {
      const res = await SourcingRfpService.runBidAnalysis(selectedRfpId);
      if (res.success) {
        setAiAnalysis(res.data);
        showSuccess('AI Bid Analysis completed successfully.');
      }
    } catch (err) {
      showError(err.message || 'Failed to run AI Bid Analysis.');
    } finally {
      setRunningAi(false);
    }
  };

  const scoredResponses = responses.filter((res) =>
    Number.isFinite(Number(res.evaluationId?.weightedScore)),
  );

  const unscoredResponses = responses.filter(
    (res) => !Number.isFinite(Number(res.evaluationId?.weightedScore)),
  );

  const technicalAverage = scoredResponses.length
    ? scoredResponses.reduce(
        (sum, res) => sum + Number(res.evaluationId?.technicalScore || 0),
        0,
      ) / scoredResponses.length
    : 0;

  const commercialAverage = scoredResponses.length
    ? scoredResponses.reduce(
        (sum, res) => sum + Number(res.evaluationId?.commercialScore || 0),
        0,
      ) / scoredResponses.length
    : 0;

  const weightedAverage = scoredResponses.length
    ? scoredResponses.reduce(
        (sum, res) => sum + Number(res.evaluationId?.weightedScore || 0),
        0,
      ) / scoredResponses.length
    : 0;

  const commercialRanking = [...scoredResponses]
    .sort(
      (a, b) =>
        Number(b.evaluationId?.weightedScore || 0) -
        Number(a.evaluationId?.weightedScore || 0),
    )
    .slice(0, 5);

  const handleFinalize = async () => {
    if (!selectedRfpId) {
      showError('Select an RFP event before finalizing evaluation.');
      return;
    }

    if (scoredResponses.length === 0) {
      showError('Score at least one vendor before finalizing.');
      return;
    }

    try {
      await SourcingRfpService.updateRfp(selectedRfpId, {
        status: 'EVALUATED',
        evaluationCompletedAt: new Date().toISOString(),
      });
      showSuccess('Evaluation finalized successfully.');
      navigate(`/sourcing/evaluation-award/${selectedRfpId}?tab=award`);
    } catch (err) {
      showError(
        err.message || 'Finalize is currently unavailable from backend.',
      );
    }
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'rfpInfo',
      label: 'Sourcing Event',
      // Only show this column if we are looking at "All Data"
      hidden: !!selectedRfpId,
      render: (_, res) => (
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-gray-400" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-700">
              {res.rfpId?.title || 'N/A'}
            </span>
            <span className="text-xs text-gray-400">
              {res.rfpId?.rfpNumber}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'vendor',
      label: 'Vendor',
      render: (_, res) => (
        <div className="flex flex-col">
          <span className="font-bold text-gray-900">
            {res.vendorId?.name || 'Unknown Vendor'}
          </span>
          <span className="text-xs text-gray-500">
            {res.vendorId?.email || 'No email'}
          </span>
        </div>
      ),
    },
    {
      key: 'totalValue',
      label: 'Bid Amount',
      render: (_, res) => {
        const total = res.lineItems?.reduce(
          (sum, item) => sum + (item.price || 0),
          0,
        );
        return (
          <span className="text-blue-600 font-bold">
            ${total?.toLocaleString()}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, res) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
            res.status === 'SUBMITTED'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          {res.status}
        </span>
      ),
    },
    {
      key: 'score',
      label: 'Score',
      render: (_, res) => {
        const score = res.evaluationId?.weightedScore;
        return (
          <div className="flex flex-col">
            {score !== undefined ? (
              <span className="font-bold text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-100 w-fit">
                {score.toFixed(2)}%
              </span>
            ) : (
              <span className="text-gray-400 italic text-sm">Not Scored</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, res) => (
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/sourcing/responses/view/${res._id}`)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => openEvalModal(res)}
            className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg flex items-center gap-1"
          >
            <Star size={16} /> Score Vendor
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      {!isNested && <PageBreadcrumb />}
      {!isNested && (
        <PageHeader
          title="Evaluation Dashboard"
          description={
            selectedRfpId
              ? 'Technical scoring and commercial comparison for selected event'
              : 'Technical scoring and commercial comparison across sourcing events'
          }
          actions={[
            {
              icon: (
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              ),
              variant: 'outline',
              onClick: loadInitialData,
            },
            {
              label: 'Score Vendor',
              icon: <Star size={16} />,
              variant: 'outline',
              disabled: responses.length === 0,
              onClick: () => {
                const candidate = unscoredResponses[0] || responses[0];
                if (!candidate) {
                  showError('No vendor response available for scoring.');
                  return;
                }
                openEvalModal(candidate);
              },
            },
            {
              label: 'Finalize',
              icon: <CheckCircle2 size={16} />,
              variant: 'primary',
              disabled: !selectedRfpId || scoredResponses.length === 0,
              onClick: handleFinalize,
            },
          ]}
        />
      )}

      <div className={isNested ? "" : "page-container"}>
        {/* Selector Section - Hide if nested and we have a prop-passed ID */}
        {(!isNested || !propRfpId) && (
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-3 text-gray-400">
              <Filter size={20} />
              <span className="text-sm font-semibold text-gray-600 whitespace-nowrap">
                Filter by Event:
              </span>
            </div>
            <select
              className="flex-1 bg-gray-50 border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              value={selectedRfpId}
              onChange={(e) => onRfpSelect(e.target.value)}
            >
              <option value="">Show All Responses</option>
              {rfps
                .filter((r) => r.status === 'PUBLISHED' || r.status === 'DRAFT')
                .map((rfp) => (
                  <option key={rfp._id} value={rfp._id}>
                    {rfp.rfpNumber} - {rfp.title} ({rfp.status})
                  </option>
                ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <ListChecks size={18} className="text-blue-600" />
              <h3 className="text-base font-semibold text-gray-800">
                Technical Scoring
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500 mb-1">Technical Weight</p>
                <p className="text-lg font-bold text-gray-900">
                  {Math.round(TECHNICAL_WEIGHT * 100)}%
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500 mb-1">
                  Avg Technical Score
                </p>
                <p className="text-lg font-bold text-blue-700">
                  {technicalAverage.toFixed(2)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs text-gray-500 mb-1">Avg Weighted Score</p>
                <p className="text-lg font-bold text-emerald-700">
                  {weightedAverage.toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <Scale size={18} className="text-purple-600" />
              <h3 className="text-base font-semibold text-gray-800">
                Commercial Comparison
              </h3>
            </div>
            {commercialRanking.length === 0 ? (
              <p className="text-sm text-gray-500">
                No scored vendors to compare yet.
              </p>
            ) : (
              <div className="space-y-2">
                {commercialRanking.map((res, idx) => (
                  <div
                    key={res._id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Trophy
                        size={14}
                        className={
                          idx === 0 ? 'text-amber-500' : 'text-gray-400'
                        }
                      />
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {res.vendorId?.name || 'Unknown Vendor'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Bid: ${toBidAmount(res).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">Commercial Avg</p>
                      <p className="text-sm font-bold text-purple-700">
                        {Number(res.evaluationId?.commercialScore || 0).toFixed(
                          2,
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 text-xs text-gray-500">
              Weighted scoring: {(TECHNICAL_WEIGHT * 100).toFixed(0)}% Technical
              + {(COMMERCIAL_WEIGHT * 100).toFixed(0)}% Commercial. Current
              commercial average: {commercialAverage.toFixed(2)}.
            </div>
          </div>
        </div>

        {/* AI Bid Analysis Panel */}
        {selectedRfpId && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BrainCircuit size={18} className="text-indigo-600" />
                <h3 className="text-base font-semibold text-gray-800">AI Bid Analysis</h3>
              </div>
              <button
                onClick={handleRunAiAnalysis}
                disabled={runningAi || responses.length === 0}
                className="px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {runningAi ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <BrainCircuit size={16} />
                )}
                {runningAi ? 'Analyzing...' : 'Run Analysis'}
              </button>
            </div>

            {!aiAnalysis ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <BrainCircuit size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-600">No AI analysis available for this RFP.</p>
                <p className="text-xs text-gray-500 mt-1">Click 'Run Analysis' to automatically evaluate vendor bids based on your configured QCBS weighting.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3">Vendor</th>
                        <th className="px-4 py-3 text-center">Eligibility</th>
                        <th className="px-4 py-3 text-center">Tech Score</th>
                        <th className="px-4 py-3">Deviation Flags</th>
                        <th className="px-4 py-3 text-right">Financial Bid</th>
                        <th className="px-4 py-3 text-right">Financial Score</th>
                        <th className="px-4 py-3 text-right">Combined Score</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {aiAnalysis.perVendor?.map((vendorAnalysis, idx) => {
                        const vendorIdStr = vendorAnalysis.vendor?._id?.toString() || vendorAnalysis.vendor?.toString();
                        const recVendorIdStr = aiAnalysis.recommendation?.recommendedVendor?._id?.toString() || aiAnalysis.recommendation?.recommendedVendor?.toString();
                        const isRecommended = recVendorIdStr === vendorIdStr;
                        const combinedScoreObj = aiAnalysis.recommendation?.combinedScores?.find(cs => cs.vendor?.toString() === vendorIdStr);
                        return (
                          <tr key={idx} className={isRecommended ? 'bg-indigo-50/50' : 'bg-white'}>
                            <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2">
                              {isRecommended && <Trophy size={16} className="text-amber-500" />}
                              {vendorAnalysis.vendor?.companyName || vendorAnalysis.vendor?.name || 'Unknown Vendor'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {vendorAnalysis.eligibilityCompliant ? (
                                <CheckCircle size={16} className="mx-auto text-emerald-500" />
                              ) : (
                                <XCircle size={16} className="mx-auto text-red-500" />
                              )}
                            </td>
                            <td className="px-4 py-3 text-center font-semibold">
                              {vendorAnalysis.technicalComplianceScore}%
                            </td>
                            <td className="px-4 py-3">
                              {vendorAnalysis.deviationFlags?.length > 0 ? (
                                <ul className="list-disc list-inside text-xs text-amber-600">
                                  {vendorAnalysis.deviationFlags.map((flag, i) => (
                                    <li key={i}>{flag}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-xs text-gray-400 italic">None</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                              {vendorAnalysis.financialBid ? `$${vendorAnalysis.financialBid.toLocaleString()}` : 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-right font-bold text-indigo-700 whitespace-nowrap">
                              {combinedScoreObj ? combinedScoreObj.combinedScore.toFixed(2) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100 flex items-start gap-3">
                  <BrainCircuit className="text-indigo-600 shrink-0 mt-0.5" size={20} />
                  <div>
                    <h4 className="font-semibold text-indigo-900 text-sm mb-1">AI Recommendation Summary</h4>
                    <p className="text-sm text-indigo-800 leading-relaxed">
                      {aiAnalysis.recommendation?.summary || 'No summary available.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {evalModal.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <h3 className="text-lg font-bold text-gray-800">
                  Evaluate: {evalModal.response.vendorId?.name}
                </h3>
                <button onClick={() => setEvalModal({ open: false })}>
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Technical Score (0-100)
                  </label>
                  <input
                    type="number"
                    className="w-full border rounded-lg p-2"
                    min="0"
                    max="100"
                    value={scores.technical}
                    onChange={(e) =>
                      setScores({ ...scores, technical: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Commercial Score (0-100)
                  </label>
                  <input
                    type="number"
                    className="w-full border rounded-lg p-2"
                    min="0"
                    max="100"
                    value={scores.commercial}
                    onChange={(e) =>
                      setScores({ ...scores, commercial: e.target.value })
                    }
                  />
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                  Weighted Score = (Technical x{' '}
                  {(TECHNICAL_WEIGHT * 100).toFixed(0)}%) + (Commercial x{' '}
                  {(COMMERCIAL_WEIGHT * 100).toFixed(0)}%) ={' '}
                  {(
                    clampScore(scores.technical) * TECHNICAL_WEIGHT +
                    clampScore(scores.commercial) * COMMERCIAL_WEIGHT
                  ).toFixed(2)}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Evaluator Comments
                  </label>
                  <textarea
                    className="w-full border rounded-lg p-2 h-24"
                    value={scores.comments}
                    onChange={(e) =>
                      setScores({ ...scores, comments: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="p-6 bg-gray-50 flex gap-3">
                <button
                  onClick={() => setEvalModal({ open: false })}
                  className="flex-1 py-2 border rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScoreSubmit}
                  className="flex-1 py-2 bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Save size={18} /> Score Vendor
                </button>
              </div>
            </div>
          </div>
        )}
        {/* The Table now always shows data (either filtered or all) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <DataTable
            data={responses}
            showActions={false}
            // Filter columns to remove hidden ones (like rfpInfo when an RFP is selected)
            columns={columns.filter((col) => !col.hidden)}
            loading={loading}
            emptyMessage={
              selectedRfpId
                ? 'No bids for this RFP yet.'
                : 'No vendor responses found in the system.'
            }
          />
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default BidEvaluation;
